// ** React Imports
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

// ** Third Party
import toast from "react-hot-toast";
import { isNilOrEmpty } from "@salespark/toolkit";

// ** Data / hooks / utils (reused, never duplicated)
import { course_get, course_update, ping_post } from "src/api/courses";
import { useAuth } from "src/hooks/useAuth";
import { searchSuffixWithoutToken } from "src/@core/utils/urlQueryPreservation";

/******************************************************************
 * ##: useFlixCoursePlayer
 * Course-player orchestration for the Flix mode. Re-implements the
 * data/state/handlers of the current player (src/pages/courses) while
 * reusing the SAME API functions (course_get / course_update /
 * ping_post) and utils. Production files are left untouched; the only
 * behavioural difference is that navigation stays inside /flix.
 * Returns the exact prop contract a player shell consumes.
 * @returns {Object} - player state + handlers (see return statement)
 * History:
 * 22-07-2026: Created
 ******************************************************************/
export const useFlixCoursePlayer = () => {
  const auth = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState({});
  const [actualChapter, setActualChapter] = useState(null);
  const [actualLesson, setActualLesson] = useState(null);
  const [isFirstLesson, setIsFirstLesson] = useState(true);
  const [isLastLesson, setIsLastLesson] = useState(false);
  const [lessonsArray, setLessonsArray] = useState([]);
  const [lessonsStatus, setLessonsStatus] = useState([]);
  const [errorLoadingData, setErrorLoadingData] = useState({ visible: false, message: "", code: -1 });
  const [noLessonsAvaliable, setNoLessonsAvaliable] = useState(false);
  const [videosInfos, setVideosInfos] = useState([]);

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const course_id = params?.course_id;
  const module_id = params?.module_id; // present only when opened from a module card (/module/:module_id)
  const chapter_id = params?.chapter_id;
  const lesson_id = params?.lesson_id;

  const authUser = auth?.user;
  const pingContextRef = useRef({ authUser: null, course_id: null, chapter: null, lesson: null });

  useEffect(() => {
    if (course_id && authUser) loadCourse(course_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_id, authUser]);

  useEffect(() => {
    pingContextRef.current = {
      authUser,
      course_id,
      chapter: actualChapter,
      lesson: actualLesson,
    };
  }, [authUser, course_id, actualChapter, actualLesson]);

  const ping = async () => {
    try {
      const { authUser: currentUser, course_id: currentCourseId, chapter, lesson } = pingContextRef.current;
      if (isNilOrEmpty(currentUser) || isNilOrEmpty(currentCourseId)) return;
      if (isNilOrEmpty(chapter) || isNilOrEmpty(lesson)) return;
      await ping_post(currentUser, currentCourseId, { chapter, lesson });
    } catch (error) {
      // Keepalive failures are non-fatal
    }
  };

  useEffect(() => {
    ping();
    const intervalId = setInterval(() => {
      ping();
    }, 60000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourse = async (courseId) => {
    try {
      setErrorLoadingData({ visible: false, message: "", code: -1 });
      setIsLoading(true);

      const response = await dbGetCourse(authUser, courseId);
      if (!response.status) {
        if (response.data?.response?.data?.code === 6624) {
          setErrorLoadingData({ visible: true, message: "Já terminou o tempo de acesso a este curso.", code: 6624 });
        } else if (response.data?.response?.data?.code === 6623) {
          setErrorLoadingData({
            visible: true,
            message: "Não tens acesso a este curso, o curso não existe ou não se encontra disponível de momento.",
            code: 6623,
          });
        } else {
          const message = response.data?.response?.data?.message || "Não foi possível carregar o curso. Tenta novamente mais tarde.";
          setErrorLoadingData({ visible: true, message, code: -1 });
        }
        setIsLoading(false);
        return;
      }

      const { course: courseObj, details, videos_info } = response.data;

      // Flix module scope: opened from a module card (/module/:module_id) => show ONLY that
      // module. Public (no-turma) modules are their own cards now, so nothing is folded in.
      // Empty scope (stale or removed module) falls back to the full course so we never dead-end.
      const inScope = (chapter) => String(chapter?.chapter_id) === String(module_id);
      let scopedDetails = module_id ? details.filter(inScope) : details;
      if (scopedDetails.length === 0) scopedDetails = details;

      if (scopedDetails.length === 0) {
        setIsLoading(false);
        setNoLessonsAvaliable(true);
        return;
      }

      // Resolve the active lesson within the scope.
      let _actualChapter;
      let _actualLesson;
      if (module_id) {
        // Module card: URL lesson -> saved pointer (if inside scope) -> the target module's first lesson.
        const scopedLessonIds = new Set(scopedDetails.flatMap((chapter) => chapter.lessons.map((lesson) => lesson.lesson_id)));
        const targetModule = scopedDetails.find((chapter) => String(chapter.chapter_id) === String(module_id)) || scopedDetails[0];
        _actualLesson = lesson_id
          ? lesson_id
          : scopedLessonIds.has(courseObj.actual_lesson)
          ? courseObj.actual_lesson
          : targetModule.lessons[0].lesson_id;
        const holder = scopedDetails.find((chapter) => chapter.lessons.some((lesson) => String(lesson.lesson_id) === String(_actualLesson)));
        _actualChapter = holder ? holder.chapter_id : targetModule.chapter_id;
      } else {
        _actualChapter = chapter_id || courseObj.actual_chapter || scopedDetails[0].chapter_id;
        _actualLesson = lesson_id || courseObj.actual_lesson || scopedDetails[0].lessons[0].lesson_id;
      }

      const _lessonsArray = scopedDetails.map((chapter) =>
        chapter.lessons.map((lesson) => ({ chapter: chapter.chapter_id, lesson: lesson.lesson_id })),
      );
      const __lessonsArray = [].concat.apply([], _lessonsArray);

      setLessonsArray(__lessonsArray);
      updateFirstLastLesson(__lessonsArray, _actualLesson);

      setCourse({ ...response.data, details: scopedDetails });
      setVideosInfos(videos_info);
      setLessonsStatus(courseObj.lessonStatus || []);

      // Remember this as the last opened Flix course (for the catalog hero)
      rememberLastOpenedCourse(courseId);

      dbUpdateCourse(authUser, courseId, "global", {
        started: true,
        actual_chapter: _actualChapter,
        actual_lesson: _actualLesson,
      });
      setActiveLesson(_actualChapter, _actualLesson);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setErrorLoadingData({ visible: true, message: "Não foi possível carregar o curso. Tenta novamente mais tarde.", code: -1 });
    }
  };

  const updateFirstLastLesson = (lessons, lesson) => {
    try {
      if (lessons.length > 0) {
        // Normalize IDs before comparing: on initial load `lesson` comes from the URL
        // params as a string, while lesson IDs in the course data are numbers.
        const _lesson = parseInt(lesson);
        const _isFirstLesson = parseInt(lessons[0].lesson) === _lesson;
        const _isLastLesson = parseInt(lessons[lessons.length - 1].lesson) === _lesson;
        setIsFirstLesson(_isFirstLesson);
        setIsLastLesson(_isLastLesson);
      }
    } catch (error) {
      // no-op
    }
  };

  const setActiveLesson = (chapter, lesson) => {
    setActualChapter(parseInt(chapter));
    setActualLesson(parseInt(lesson));
    updateFirstLastLesson(lessonsArray, lesson);
    // Module cards carry only the lesson in the URL (the module is fixed); normal mode keeps chapter + lesson.
    const base = module_id ? `/flix/courses/${course_id}/module/${module_id}` : `/flix/courses/${course_id}`;
    const suffix = module_id ? `/${lesson}` : `/${chapter}/${lesson}`;
    navigate(`${base}${suffix}${searchSuffixWithoutToken(location.search)}`, { replace: true });
  };

  const dbGetCourse = (authData, courseId) => {
    return new Promise((resolve) => {
      try {
        course_get(authData, courseId)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  /** Sync playlist completion from API after questionnaire submit (server updates lessonStatus on POST). */
  const refreshLessonsStatus = async () => {
    const response = await dbGetCourse(authUser, course_id);
    if (response.status !== true) return;
    const innerCourse = response.data?.course;
    if (innerCourse?.lessonStatus != null) {
      setLessonsStatus(innerCourse.lessonStatus || []);
    }
  };

  const dbUpdateCourse = (authData, courseId, action, payload) => {
    return new Promise((resolve) => {
      try {
        course_update(authData, courseId, action, payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const findLessonMetaById = (lessonIdNum) => {
    const chapters = course?.details;
    if (Array.isArray(chapters) === false) return null;
    for (const ch of chapters) {
      const hit = ch.lessons?.find((l) => l.lesson_id === lessonIdNum);
      if (hit) return hit;
    }
    return null;
  };

  const lessonStatusHandler = async (chapterId, lessonId, completed, meta = {}) => {
    const lessonMeta = findLessonMetaById(lessonId);
    if (lessonMeta?.lesson_type === "questionnaire") {
      if (completed !== true || meta?.fromQuestionnaireSubmit !== true) return;
    }

    let previousStatus = [];
    setLessonsStatus((prev) => {
      previousStatus = prev;
      const filtered = prev.filter((status) => status.lesson_id !== lessonId);
      return [...filtered, { lesson_id: lessonId, completed }];
    });

    const response = await dbUpdateCourse(authUser, course_id, "updateLessonStatus", { lesson_id: lessonId, completed });
    if (!response.status) {
      toast.error("Oops... Erro ao atualizar estado da aula...", { position: "top-right" });
      setLessonsStatus(previousStatus);
      return;
    }
    setLessonsStatus(response.data.lessonStatus);
  };

  const activeLessonHandler = (chapter, lessonId) => {
    setActiveLesson(chapter, lessonId);
    updateFirstLastLesson(lessonsArray, lessonId);
    dbUpdateCourse(authUser, course_id, "global", { actual_chapter: chapter, actual_lesson: lessonId });
  };

  const updateVideoCurrentTime = (chapter, lessonId, videoId, currentTime) => {
    if (!videoId || !currentTime) return;
    dbUpdateCourse(authUser, course_id, "updateVideoCurrentTime", { video_id: videoId, current_time: currentTime });
  };

  const updateAudioCurrentTime = (lessonId, currentTime) => {
    if (!lessonId || !currentTime) return;
    dbUpdateCourse(authUser, course_id, "updateAudioCurrentTime", { lesson_id: lessonId, current_time: currentTime });
  };

  const moveToPreviousLesson = () => {
    course.details.forEach((chapter) => {
      chapter.lessons.forEach((lesson) => {
        if (lesson.lesson_id === actualLesson) {
          const nextLesson = chapter.lessons[chapter.lessons.indexOf(lesson) - 1];
          if (nextLesson) {
            activeLessonHandler(chapter.chapter_id, nextLesson.lesson_id);
          } else {
            const nextChapter = course.details[course.details.indexOf(chapter) - 1];
            if (nextChapter) {
              activeLessonHandler(nextChapter.chapter_id, nextChapter.lessons[nextChapter.lessons.length - 1].lesson_id);
            }
          }
        }
      });
    });
  };

  const moveToNextLesson = () => {
    course.details.forEach((chapter) => {
      chapter.lessons.forEach((lesson) => {
        if (lesson.lesson_id === actualLesson) {
          const nextLesson = chapter.lessons[chapter.lessons.indexOf(lesson) + 1];
          if (nextLesson) {
            activeLessonHandler(chapter.chapter_id, nextLesson.lesson_id);
          } else {
            const nextChapter = course.details[course.details.indexOf(chapter) + 1];
            if (nextChapter) {
              activeLessonHandler(nextChapter.chapter_id, nextChapter.lessons[0].lesson_id);
            }
          }
        }
      });
    });
  };

  return {
    // state
    isLoading,
    course,
    videosInfos,
    actualChapter,
    actualLesson,
    lessonsStatus,
    isFirstLesson,
    isLastLesson,
    errorLoadingData,
    noLessonsAvaliable,
    location,
    // handlers
    moveToNextLesson,
    moveToPreviousLesson,
    updateVideoCurrentTime,
    updateAudioCurrentTime,
    lessonStatusHandler,
    activeLessonHandler,
    refreshLessonsStatus,
  };
};

/******************************************************************
 * ##: rememberLastOpenedCourse
 * Persists the last opened Flix course id in localStorage so the
 * catalog hero can feature it (the /users/courses payload has no
 * "last opened" field). Failures are swallowed (private mode /
 * storage disabled).
 * @param {String} courseId - the course just opened
 * @returns {void}
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const rememberLastOpenedCourse = (courseId) => {
  try {
    window.localStorage.setItem("flix:last_course", JSON.stringify({ id: String(courseId), at: Date.now() }));
  } catch (e) {
    // storage unavailable — non-fatal
  }
};

export default useFlixCoursePlayer;
